/* eslint-disable import/prefer-default-export */

import { LOT_ORDER_LIMIT, ORDER_TYPE } from 'constants/trading';
import { getTargetAPI } from 'features/securities/utils';
import {
  ReqAmendStopOrder,
  ReqCreateStopOrder,
  StopOrderAssetMarketBoard,
  StopOrderAssetType,
  StopOrderExpiryType,
  StopOrderOrderType,
  StopOrderProductCode,
  StopOrderSide,
  StopOrderTriggerType,
} from 'global/Securities/types/api/smartOrder';
import { SmartOrderLabel } from 'global/Securities/types/trading';
import { SplitOrderMethodEnum } from 'global/Securities/types/transaction';

export const getInitialPrice = (
  companyData,
  orderType,
  amendPreviousPrice?: number,
) => {
  const { price, orderbook } = companyData;
  const { bid, offer } = orderbook || {};
  const defaultPrice = Number(price) || 0;

  const isAmend = [ORDER_TYPE.AMEND_BUY, ORDER_TYPE.AMEND_SELL].includes(
    orderType,
  );
  if (isAmend) return amendPreviousPrice || defaultPrice;

  const bidPrice = Number(bid?.price);
  const offerPrice = Number(offer?.price);
  const isARB = Number.isNaN(bidPrice);
  const isARA = Number.isNaN(offerPrice);

  // Return orderbook price when autoreject or fallback to price field
  if (isARB) return offerPrice || defaultPrice;
  if (isARA) return bidPrice || defaultPrice;

  if (orderType === ORDER_TYPE.BUY && offerPrice) return offerPrice;
  if (orderType === ORDER_TYPE.SELL && bidPrice) return bidPrice;

  return defaultPrice;
};

export const getLotOrderLimit = () => LOT_ORDER_LIMIT[getTargetAPI()];

interface iSplitOrderPayload {
  splitOrderActive: boolean;
  splitQuantity: number;
  splitRangeMin: number;
  splitRangeMax: number;
  splitMethod: SplitOrderMethodEnum;
}

interface iSplitOrderReturn {
  split_order: {
    qty: number;
    min_lot?: number;
    max_lot?: number;
    method?: SplitOrderMethodEnum;
  };
}

export const getSplitOrderPayload = ({
  splitOrderActive,
  splitQuantity,
  splitRangeMin,
  splitRangeMax,
  splitMethod,
}: iSplitOrderPayload): iSplitOrderReturn | Record<string, never> => {
  if (!splitOrderActive) return {};
  const isRandom = splitMethod === SplitOrderMethodEnum.RANDOM;

  let splitOrder: iSplitOrderReturn['split_order'] = {
    qty: splitQuantity,
    method: splitMethod,
  };

  if (isRandom) {
    splitOrder = {
      ...splitOrder,
      min_lot: splitRangeMin,
      max_lot: splitRangeMax,
    };
  }

  return {
    split_order: splitOrder,
  };
};

type SmartOrderType = SmartOrderLabel;

const getProductCode = (type: SmartOrderType): StopOrderProductCode => {
  switch (type) {
    case SmartOrderLabel.SL:
      return StopOrderProductCode.StopLoss;
    case SmartOrderLabel.TP:
      return StopOrderProductCode.TakeProfit;
    case SmartOrderLabel.AB:
      return StopOrderProductCode.LimitIfTouched;
    default:
      return StopOrderProductCode.Unspecified;
  }
};

const getAssetType = (assetType: string): StopOrderAssetType => {
  switch (assetType) {
    case 'Saham':
      return StopOrderAssetType.Stock;
    case 'Right':
      return StopOrderAssetType.Right;
    case 'Waran':
      return StopOrderAssetType.Warrant;
    case 'Bond':
      return StopOrderAssetType.Bond;
    default:
      return StopOrderAssetType.Unspecified;
  }
};

const getAssetMarketBoard = (assetType: string): StopOrderAssetMarketBoard => {
  switch (assetType) {
    case 'Right':
      return StopOrderAssetMarketBoard.Tunai;
    default:
      return StopOrderAssetMarketBoard.Reguler;
  }
};

const getTriggerType = (type: SmartOrderType): StopOrderTriggerType => {
  switch (type) {
    case SmartOrderLabel.SL:
      return StopOrderTriggerType.LessThanEqual;
    case SmartOrderLabel.TP:
      return StopOrderTriggerType.GreaterThanEqual;
    default:
      return StopOrderTriggerType.Unspecified;
  }
};

export const generateStopOrderParams = (
  symbol: string,
  shares: number,
  price: number,
  type: SmartOrderType,
  type_company: string,
  triggerPrice?: number,
  config_type?: number,
  expiry_type?: string,
): ReqCreateStopOrder => {
  const triggerType =
    config_type === 2
      ? StopOrderTriggerType.LessThanEqual
      : StopOrderTriggerType.GreaterThanEqual;

  const finalTriggerType =
    config_type !== undefined && triggerType
      ? triggerType
      : getTriggerType(type);

  const expiryType =
    expiry_type === '1' ? StopOrderExpiryType.GTC : StopOrderExpiryType.GFD;

  const finalExpiryType =
    expiry_type !== undefined && expiryType
      ? expiryType
      : StopOrderExpiryType.GTC;

  return {
    product_code: getProductCode(type),
    asset: {
      code: symbol,
      type: getAssetType(type_company),
      market_board: getAssetMarketBoard(type_company),
    },
    shares: String(shares),
    trigger_type: finalTriggerType,
    trigger_price: String(triggerPrice),
    order_price: String(price),
    order_side: StopOrderSide.Buy,
    order_expiry_type: finalExpiryType,
    order_type: StopOrderOrderType.Limit,
  };
};

export const generateStopOrderAmendParams = (
  shares: number,
  price: number,
  triggerPrice: number,
  config_type?: number,
): ReqAmendStopOrder => {
  const triggerType =
    config_type === 2
      ? StopOrderTriggerType.LessThanEqual
      : StopOrderTriggerType.GreaterThanEqual;

  return {
    new_order_price: String(price ?? 0),
    new_shares: String(shares ?? 0),
    new_trigger_price: String(triggerPrice ?? 0),
    new_trigger_type: triggerType,
  };
};
